import useAuthStore from "../store/authStore";
import { loginApi, verify2faLoginApi, getGoogleOAuthUrlApi, handleGoogleLoginCallbackApi } from "../api/auth.api";
import { getMyProfileApi } from "../api/user.api";
import { tokenService } from "../services/tokenService";
import type { User } from "../types/user";
import type { AuthApiResponse } from "../types/auth";

const normalizeAuthPayload = (response: AuthApiResponse) => {
  const payload = response?.data || response;

  return {
    user: payload?.user || payload?.data?.user || payload?.data?.admin || null,
    accessToken:
      payload?.accessToken || payload?.token || payload?.data?.accessToken ||
      payload?.data?.token,
    refreshToken:
      payload?.refreshToken || payload?.data?.refreshToken || null,
  };
};

interface PersistSessionArgs {
  user: User | null;
  accessToken?: string;
  refreshToken?: string | null;
  setUser: (user: User | null) => void;
}

const persistSession = ({ user, accessToken, refreshToken, setUser }: PersistSessionArgs) => {
  if (accessToken) {
    tokenService.setTokens(accessToken, refreshToken);
  }

  if (user) {
    localStorage.setItem("user", JSON.stringify(user));
    setUser(user);
  }

  localStorage.setItem("isLoggedIn", "true");
};

export const useAuth = () => {
  const { setUser } = useAuthStore();

  const login = async (payload: Record<string, unknown>) => {
    const response = await loginApi(payload);
    const { user, accessToken, refreshToken } = normalizeAuthPayload(response);
    const requiresTwoFactor = Boolean(response?.data?.twoFactorRequired);
    const tempToken = response?.data?.tempToken || null;

    if (requiresTwoFactor && !accessToken) {
      return {
        requiresTwoFactor: true,
        tempToken,
        raw: response,
      };
    }

    persistSession({ user, accessToken, refreshToken, setUser });

    return {
      user,
      accessToken,
      refreshToken,
      requiresTwoFactor: false,
      raw: response,
    };
  };

  const verifyTwoFactorLogin = async (payload: { tempToken?: string | null; [key: string]: unknown }) => {
    const response = await verify2faLoginApi(payload);
    const { user, accessToken, refreshToken } = normalizeAuthPayload(response);

    persistSession({ user, accessToken, refreshToken, setUser });

    return {
      user,
      accessToken,
      refreshToken,
      raw: response,
    };
  };

  const initiateGoogleLogin = async () => {
    const response = await getGoogleOAuthUrlApi();
    const oauthUrl = response?.data?.oauthUrl || response?.oauthUrl;

    if (!oauthUrl) {
      throw new Error("Failed to get Google OAuth URL");
    }

    return oauthUrl;
  };

  const completeGoogleLogin = async ({ code, state }: { code: string; state?: string | null }) => {
    const response = await handleGoogleLoginCallbackApi({ code, state });
    console.log("[Google OAuth] callback response:", response);
    const { user, accessToken, refreshToken } = normalizeAuthPayload(response);

    persistSession({ user, accessToken, refreshToken, setUser });

    return {
      user,
      accessToken,
      refreshToken,
      raw: response,
    };
  };

  // Apple's web OAuth callback (GET /api/auth/apple/callback) authenticates the
  // user server-side and redirects here with just a short-lived access token
  // (no refresh token, no user payload) — fetch the profile with it and persist
  // the same session shape the email/Google flows use.
  const completeAppleLogin = async (token: string) => {
    tokenService.setTokens(token);

    const profileResponse = await getMyProfileApi();
    const user = profileResponse?.data || profileResponse?.user || null;

    persistSession({ user, accessToken: token, refreshToken: null, setUser });

    return {
      user,
      accessToken: token,
      raw: profileResponse,
    };
  };

  return {
    login,
    verifyTwoFactorLogin,
    initiateGoogleLogin,
    completeGoogleLogin,
    completeAppleLogin,
  };
};
